import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useNavigate } from 'react-router-dom';
import { CRITUTILS } from "../../config/API";
import jsPDF from 'jspdf';

const RenewalBusinessLicense = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        // Calendar Year
        calendarYear: new Date().getFullYear().toString(),

        // License Type
        licenseType: '',
        temporaryStartDate: '',
        seasonalStartDate: '',
        peddlerType: '',
        expeditedProcessing: false,

        // Reason for Applying
        applicationReason: '',
        previousNameLocation: '',
        ownerChangeDetails: '',

        // Business Information
        legalBusinessName: '',
        federalEIN: '',
        physicalAddress: '',
        mailingAddress: '',
        businessPhone: '',
        businessFax: '',
        businessEmail: '',
        commenceDate: '',
        businessDescription: '',
        businessActivity: '',
        tobaccoSales: '',
        liquorSales: '',
        previousLicense: '',
        previousLicenseNumber: '',
        previousBusinessName: '',
        licenseSuspended: '',
        licenseSuspendedDetails: '',
        entityType: '',
        incorporationState: '',

        // Owner Information
        owners: [{ name: '', percentage: '' }],
        ownerMailingAddress: '',
        ownerPhone: '',
        ownerFax: '',
        ownerEmail: '',
        primaryContactFirstName: '',
        primaryContactLastName: '',
        primaryContactDOB: '',
        primaryContactPhone: '',
        primaryContactEmail: '',

        // Additional Business Information
        agentName: '',
        agentAddress: '',
        critLease: '',
        leaseNumber: '',
        leaseAddress: '',
        critMember: '',
        enrollmentNumber: '',
        otherTribe: '',
        otherTribeName: '',

        // Consent
        signature: '',
        printName: '',
        signatureDate: '',
        agentSignature: '',
        agentSignatureDate: ''
    });

    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');
    const [error, setError] = useState('');
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const [taxpayerId, setTaxpayerId] = useState('');
    const [apiData, setApiData] = useState(null);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [submittedData, setSubmittedData] = useState(null); // Store submitted data for PDF

    // Refs for signature pads
    const signaturePadRef = useRef(null);
    const agentSignaturePadRef = useRef(null);

    useEffect(() => {
        const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
        console.log("Stored Taxpayer ID:", storedTaxpayerId);

        if (storedTaxpayerId) {
            setTaxpayerId(storedTaxpayerId);
            fetchTaxpayerData(storedTaxpayerId);
        } else {
            setTaxpayerId('109');
            fetchTaxpayerData('109');
        }
    }, []);

    const fetchTaxpayerData = async (taxpayerId) => {
        try {
            setFetchLoading(true);
            const apiUrl = `${CRITUTILS}?taxpayer=${taxpayerId}&action=gettaxpayerinfo`;

            const response = await fetch(apiUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.Status === "Success") {
                setApiData(result.Message);
                populateFormData(result.Message);
            } else {
                console.error('Failed to fetch taxpayer data:', result.Message);
            }
        } catch (error) {
            console.error('Error fetching taxpayer data:', error);
            setError('Failed to load existing business data. Please refresh the page.');
        } finally {
            setFetchLoading(false);
        }
    };

    const populateFormData = (apiData) => {
        const { Taxpayer, BusinessLicense } = apiData;

        // Map license types from API to form values
        const getLicenseType = (apiLicenseType) => {
            switch (apiLicenseType?.toLowerCase()) {
                case 'temporary': return 'Temporary';
                case 'seasonal': return 'Seasonal';
                case 'annual': return 'Annual';
                case 'peddler': return 'Peddler';
                default: return '';
            }
        };

        // Map entity types from API to form values
        const getEntityType = (apiEntityType) => {
            if (!apiEntityType) return '';
            return apiEntityType.toLowerCase().replace(/\s+/g, '-');
        };

        // Helper to parse Primary Contact (format: "Last, First") into firstName and lastName - same as DisplayProfile.js
        const parsePrimaryContact = (primaryContact) => {
            if (!primaryContact || typeof primaryContact !== "string") {
                return { firstName: "", lastName: "" };
            }
            const parts = primaryContact.split(",").map(part => part.trim());
            if (parts.length >= 2) {
                return {
                    lastName: parts[0] || "",
                    firstName: parts[1] || "",
                };
            }
            return {
                lastName: primaryContact,
                firstName: "",
            };
        };

        // Parse owner information
        const parseOwners = (ownerNames) => {
            if (!ownerNames) return [{ name: '', percentage: '' }];
            // Assuming owner names are separated by commas
            return ownerNames.split(',').map(name => ({
                name: name.trim(),
                percentage: '' // Percentage might not be available from API
            }));
        };

        // Map CRIT member status
        const getCritMemberStatus = (critMember) => {
            if (critMember === 'Yes' || critMember === 'yes') return 'yes';
            if (critMember === 'No' || critMember === 'no') return 'no';
            return '';
        };

        // Map other tribe status
        const getOtherTribeStatus = (otherTribe) => {
            if (otherTribe === 'Yes' || otherTribe === 'yes') return 'yes';
            if (otherTribe === 'No' || otherTribe === 'no') return 'no';
            return '';
        };

        // Map lease status
        const getLeaseStatus = (leaseInfo) => {
            if (leaseInfo === 'Yes' || leaseInfo === 'yes' || leaseInfo) return 'yes';
            return 'no';
        };

        // Format phone numbers from API
        const formatPhoneFromAPI = (phone) => {
            if (!phone) return '';
            const cleaned = phone.replace(/\D/g, '');
            return formatPhoneNumber(cleaned);
        };

        // Parse primary contact from same key as DisplayProfile.js ("Primary Contact" - format "Last, First")
        const { firstName: primaryContactFirstName, lastName: primaryContactLastName } = parsePrimaryContact(
            Taxpayer?.["Primary Contact"] || BusinessLicense?.["Primary Contact"] || ""
        );

        // Helper function to build full physical address
        const buildPhysicalAddress = () => {
            const address = Taxpayer?.["Business Address"] || '';
            const city = Taxpayer?.["City"] || '';
            const state = Taxpayer?.["State"] || '';
            const zipCode = Taxpayer?.["Zip Code"] || '';
            
            const parts = [address, city, state, zipCode].filter(part => part.trim() !== '');
            return parts.join(', ');
        };

        setFormData(prev => ({
            ...prev,
            // Business Information
            legalBusinessName: Taxpayer?.["Business Name"] || '',
            federalEIN: Taxpayer?.["TIN/SSN"] || '',
            physicalAddress: buildPhysicalAddress(),
            mailingAddress: Taxpayer?.["Owner Mailing Address"] || Taxpayer?.["Business Address "] || '',
            businessPhone: formatPhoneFromAPI(Taxpayer?.["Business Phone #"]),
            businessFax: formatPhoneFromAPI(Taxpayer?.["Business Fax #"]),
            businessEmail: Taxpayer?.["Business Email Address"] || '',
            commenceDate: Taxpayer?.["Date Business to Commence"] || '',
            businessDescription: Taxpayer?.["Business Description"] || '',
            businessActivity: BusinessLicense?.["Description of Business Activity"] || Taxpayer?.["Business Description"] || '',
            entityType: getEntityType(Taxpayer?.["Type Of Entity"]),
            incorporationState: Taxpayer?.["If Corporation, State"] || '',
            previousLicenseNumber: BusinessLicense?.["Business License Number"] || BusinessLicense?.["If yes, BL#"] || '',

            // License Type
            licenseType: getLicenseType(BusinessLicense?.["Business License Type"]),

            // Owner Information
            owners: parseOwners(Taxpayer?.["Owner Name(s)"]),
            ownerMailingAddress: Taxpayer?.["Owner Mailing Address"] || Taxpayer?.["PC Mailing Address"] || '',
            ownerPhone: formatPhoneFromAPI(Taxpayer?.["Owner Phone #"] || Taxpayer?.["PC Phone #"]),
            ownerFax: formatPhoneFromAPI(Taxpayer?.["Owner Fax #"]),
            ownerEmail: Taxpayer?.["Owner Email Address"] || Taxpayer?.["PC Email Address"] || '',
            primaryContactFirstName,
            primaryContactLastName,
            primaryContactDOB: '',
            primaryContactPhone: formatPhoneFromAPI(Taxpayer?.["PC Phone #"]),
            primaryContactEmail: Taxpayer?.["PC Email Address"] || '',

            // Additional Business Information
            agentName: Taxpayer?.["Agent Name"] || '',
            critMember: getCritMemberStatus(Taxpayer?.["CRIT Tribal Member?"]),
            enrollmentNumber: Taxpayer?.["If Yes, Enrollment #"] || '',
            otherTribe: getOtherTribeStatus(Taxpayer?.["Enrolled in Another Tribe?"]),
            otherTribeName: Taxpayer?.["If Yes, Name of Tribe"] || '',
            critLease: getLeaseStatus(Taxpayer?.["CRIT Lease/Contract"]),
            leaseNumber: Taxpayer?.["Lease or Contract #"] || '',
            leaseAddress: Taxpayer?.["Lease/Contract Address"] || '',

            // Set default values for required fields that might not come from API
            applicationReason: 'renewal',
            previousBusinessName: Taxpayer?.["Previously Applied Under Another Name"] ? 'yes' : 'no',
            licenseSuspended: BusinessLicense?.["License Ever Suspended/Revoked/Denied"] ? 'yes' : 'no',
            tobaccoSales: Taxpayer?.["Tax Types"]?.includes('Tobacco') ? 'retailer' : '',
            liquorSales: ''
        }));
    };

    // US Phone Number Formatting Function
    const formatPhoneNumber = (value) => {
        // Remove all non-digits
        const cleaned = value.replace(/\D/g, '');

        // Limit to 10 digits
        const limited = cleaned.slice(0, 10);

        // Apply formatting based on length
        if (limited.length <= 3) {
            return limited;
        } else if (limited.length <= 6) {
            return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
        } else {
            return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
        }
    };

    // Auto-copy function for business information
    const handleBusinessInfoChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Apply phone number formatting for specific fields
        if (name === 'businessPhone' || name === 'businessFax' ||
            name === 'ownerPhone' || name === 'ownerFax' ||
            name === 'primaryContactPhone') {
            const formattedValue = formatPhoneNumber(value);
            setFormData(prev => ({
                ...prev,
                [name]: formattedValue
            }));

            // Auto-copy business phone to owner phone and primary contact phone
            if (name === 'businessPhone' && formattedValue) {
                setFormData(prev => ({
                    ...prev,
                    ownerPhone: formattedValue,
                    primaryContactPhone: formattedValue
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));

            // Auto-copy business email to owner email and primary contact email
            if (name === 'businessEmail' && value) {
                setFormData(prev => ({
                    ...prev,
                    ownerEmail: value,
                    primaryContactEmail: value
                }));
            }

            // Auto-copy physical address to owner mailing address
            if (name === 'physicalAddress' && value) {
                setFormData(prev => ({
                    ...prev,
                    ownerMailingAddress: value
                }));
            }
        }
    };

    const handleInputChange = (e) => {
        handleBusinessInfoChange(e);
    };

    const handleOwnerChange = (index, field, value) => {
        const updatedOwners = [...formData.owners];
        updatedOwners[index] = { ...updatedOwners[index], [field]: value };
        setFormData(prev => ({ ...prev, owners: updatedOwners }));
    };

    const addOwner = () => {
        setFormData(prev => ({
            ...prev,
            owners: [...prev.owners, { name: '', percentage: '' }]
        }));
    };

    const removeOwner = (index) => {
        if (formData.owners.length > 1) {
            const updatedOwners = formData.owners.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, owners: updatedOwners }));
        }
    };

    // Handle signature clear
    const clearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
            setFormData(prev => ({ ...prev, signature: '' }));
        }
    };

    // Handle agent signature clear
    const clearAgentSignature = () => {
        if (agentSignaturePadRef.current) {
            agentSignaturePadRef.current.clear();
            setFormData(prev => ({ ...prev, agentSignature: '' }));
        }
    };

    // Handle signature end (when user finishes signing)
    const handleSignatureEnd = () => {
        if (signaturePadRef.current) {
            const signatureData = signaturePadRef.current.toDataURL();
            setFormData(prev => ({ ...prev, signature: signatureData }));
        }
    };

    // Handle agent signature end
    const handleAgentSignatureEnd = () => {
        if (agentSignaturePadRef.current) {
            const signatureData = agentSignaturePadRef.current.toDataURL();
            setFormData(prev => ({ ...prev, agentSignature: signatureData }));
        }
    };

    const calculateFee = (data = formData) => {
        let fee = 0;

        switch (data.licenseType) {
            case 'Temporary': fee += 50; break;
            case 'Seasonal': fee += 75; break;
            case 'Annual': fee += 125; break;
            case 'peddler-retail': fee += 100; break;
            case 'peddler-food': fee += 150; break;
            default: break;
        }

        if (data.expeditedProcessing) fee += 30;
        if (data.applicationReason === 'change-name') fee += 100;
        if (data.applicationReason === 'change-owner') fee += 100;

        return fee;
    };

    const validateForm = () => {
        if (!formData.calendarYear) {
            setError('Calendar year is required.');
            return false;
        }
        if (!formData.licenseType) {
            setError('Please select a license type.');
            return false;
        }
        if (!formData.legalBusinessName) {
            setError('Legal business name is required.');
            return false;
        }
        if (!formData.federalEIN) {
            setError('Federal EIN or social security number is required.');
            return false;
        }
        if (!formData.physicalAddress) {
            setError('Physical business address is required.');
            return false;
        }
        if (!formData.previousLicenseNumber) {
            setError('Current tribal business license number is required.');
            return false;
        }
        if (!formData.signature) {
            setError('Signature is required. Please sign in the "Please sign here for renewal" section.');
            return false;
        }
        if (!formData.printName) {
            setError('Printed name is required.');
            return false;
        }
        if (!formData.signatureDate) {
            setError('Signature date is required.');
            return false;
        }

        // Validate phone number format (optional but should be valid if provided)
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
        if (formData.businessPhone && !phoneRegex.test(formData.businessPhone)) {
            setError('Business telephone number must be in format (XXX) XXX-XXXX.');
            return false;
        }
        if (formData.ownerPhone && !phoneRegex.test(formData.ownerPhone)) {
            setError('Owner telephone number must be in format (XXX) XXX-XXXX.');
            return false;
        }
        if (formData.primaryContactPhone && !phoneRegex.test(formData.primaryContactPhone)) {
            setError('Primary contact telephone number must be in format (XXX) XXX-XXXX.');
            return false;
        }

        return true;
    };

    // Convert data URL to blob for file upload
    const dataURLtoBlob = (dataURL) => {
        const byteString = atob(dataURL.split(',')[1]);
        const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    };

    // Map internal radio/checkbox values to the exact UI text shown to the user.
    // This keeps UI logic stable while sending human-readable values to the API.
    const YES_NO_DISPLAY = { yes: 'Yes', no: 'No' };

    const ENTITY_LABELS = [
        'Sole proprietorship/individual',
        'Limited liability company',
        'Educational organization',
        'Partnership',
        'Limited liability partnership',
        'Church organization',
        'Corporation',
        'Unincorporated association',
        'Joint venture',
        'Non-profit corporation',
        'Professional limited liability',
        'Tax exempt organization (501(c)(3))'
    ];

    const toEntityValue = (label) => label.toLowerCase().replace(/\s+/g, '-');

    const UI_VALUE_MAPS = {
        licenseType: {
            Temporary: 'Temporary – Engaged in business four (4) days or less',
            Seasonal: 'Seasonal – Engaged in business more than 4 days but less than 3 months',
            Annual: 'Annual – Engaged in business more than 3 months',
            Peddler: 'Peddler – Must meet the definition of peddler per business & prof. code',
        },
        peddlerType: {
            'peddler-retail': 'Retail sales (non-food) Renewal Fee: $100.00',
            'peddler-food': 'Retail sales (food) Renewal Fee: $150.00',
        },
        applicationReason: {
            renewal: 'Standard license renewal',
            'renewal-expansion': 'Renewal with business expansion',
            'change-name': 'Renewal with change of business name / location (Fee: $100.00)',
            'change-owner': 'Renewal with add/remove owner/partner (Fee: $100.00)',
        },
        businessDescription: {
            sales: 'Sales of goods',
            services: 'Services',
            both: 'Goods and services',
        },
        tobaccoSales: {
            retailer: 'Retailer',
            distributor: 'Distributor',
        },
        liquorSales: {
            retailer: 'Retailer',
            distributor: 'Distributor',
        },
        critLease: {
            yes: 'Yes - Currently leased',
            no: 'No - Not currently leased',
        },
        entityType: ENTITY_LABELS.reduce((acc, label) => {
            acc[toEntityValue(label)] = label;
            return acc;
        }, {}),
    };

    const toUiValue = (fieldName, rawValue) => {
        if (rawValue == null || rawValue === '') return '';

        // Checkbox booleans should become "Yes"/"No"
        if (typeof rawValue === 'boolean') {
            return rawValue ? 'Yes' : 'No';
        }

        const fieldMap = UI_VALUE_MAPS[fieldName];
        if (fieldMap && Object.prototype.hasOwnProperty.call(fieldMap, rawValue)) {
            return fieldMap[rawValue];
        }

        // Common yes/no radios stored as "yes"/"no"
        if (Object.prototype.hasOwnProperty.call(YES_NO_DISPLAY, rawValue)) {
            return YES_NO_DISPLAY[rawValue];
        }

        return rawValue;
    };

    const formDataToDebugObject = (fd) => {
        const out = {};
        for (const [key, val] of fd.entries()) {
            let normalized = val;
            if (val instanceof File) {
                normalized = { name: val.name, type: val.type, size: val.size };
            } else if (val instanceof Blob) {
                normalized = { type: val.type, size: val.size };
            }

            if (Object.prototype.hasOwnProperty.call(out, key)) {
                out[key] = Array.isArray(out[key]) ? [...out[key], normalized] : [out[key], normalized];
            } else {
                out[key] = normalized;
            }
        }
        return out;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitStatus('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            // Create FormData for multipart form data
            const formDataToSend = new FormData();

            // Add all form fields
            formDataToSend.append('calendarYear', formData.calendarYear);
            formDataToSend.append('licensetype', toUiValue('licenseType', formData.licenseType));
            formDataToSend.append('temporaryStartDate', formData.temporaryStartDate);
            formDataToSend.append('seasonalStartDate', formData.seasonalStartDate);
            formDataToSend.append('peddlertype', toUiValue('peddlerType', formData.peddlerType));
            formDataToSend.append('expeditedProcessing', toUiValue('expeditedProcessing', formData.expeditedProcessing));
            formDataToSend.append('applicationReason', toUiValue('applicationReason', formData.applicationReason));
            formDataToSend.append('previousNameLocation', formData.previousNameLocation);
            formDataToSend.append('ownerChangeDetails', formData.ownerChangeDetails);
            formDataToSend.append('legalBusinessName', formData.legalBusinessName);
            formDataToSend.append('federalEIN', formData.federalEIN);
            formDataToSend.append('physicalAddress', formData.physicalAddress);
            formDataToSend.append('mailingAddress', formData.mailingAddress);
            formDataToSend.append('businessPhone', formData.businessPhone);
            formDataToSend.append('businessFax', formData.businessFax);
            formDataToSend.append('businessEmail', formData.businessEmail);
            formDataToSend.append('commenceDate', formData.commenceDate);
            formDataToSend.append('businessDescription', toUiValue('businessDescription', formData.businessDescription));
            formDataToSend.append('businessActivity', formData.businessActivity);
            formDataToSend.append('tobaccoSales', toUiValue('tobaccoSales', formData.tobaccoSales));
            formDataToSend.append('liquorSales', toUiValue('liquorSales', formData.liquorSales));
            formDataToSend.append('previousLicense', toUiValue('previousLicense', formData.previousLicense));
            formDataToSend.append('previousLicenseNumber', formData.previousLicenseNumber);
            formDataToSend.append('previousBusinessName', toUiValue('previousBusinessName', formData.previousBusinessName));
            formDataToSend.append('licenseSuspended', toUiValue('licenseSuspended', formData.licenseSuspended));
            formDataToSend.append('licenseSuspendedDetails', formData.licenseSuspendedDetails);
            formDataToSend.append('entityType', toUiValue('entityType', formData.entityType));
            formDataToSend.append('incorporationState', formData.incorporationState);
            formDataToSend.append('owners', JSON.stringify(formData.owners.filter(owner => owner.name && owner.percentage)));
            formDataToSend.append('ownerMailingAddress', formData.ownerMailingAddress);
            formDataToSend.append('ownerPhone', formData.ownerPhone);
            formDataToSend.append('ownerFax', formData.ownerFax);
            formDataToSend.append('ownerEmail', formData.ownerEmail);
            formDataToSend.append('primaryContactFirstName', formData.primaryContactFirstName);
            formDataToSend.append('primaryContactLastName', formData.primaryContactLastName);
            formDataToSend.append('primaryContactDOB', formData.primaryContactDOB);
            formDataToSend.append('primaryContactPhone', formData.primaryContactPhone);
            formDataToSend.append('primaryContactEmail', formData.primaryContactEmail);
            formDataToSend.append('agentName', formData.agentName);
            formDataToSend.append('agentAddress', formData.agentAddress);
            formDataToSend.append('critLease', toUiValue('critLease', formData.critLease));
            formDataToSend.append('leaseNumber', formData.leaseNumber);
            formDataToSend.append('leaseAddress', formData.leaseAddress);
            formDataToSend.append('critMember', toUiValue('critMember', formData.critMember));
            formDataToSend.append('enrollmentNumber', formData.enrollmentNumber);
            formDataToSend.append('otherTribe', toUiValue('otherTribe', formData.otherTribe));
            formDataToSend.append('otherTribeName', formData.otherTribeName);
            formDataToSend.append('printName', formData.printName);
            formDataToSend.append('signatureDate', formData.signatureDate);
            formDataToSend.append('agentSignatureDate', formData.agentSignatureDate);
            formDataToSend.append('totalFee', calculateFee().toString());
            formDataToSend.append('submissionDate', new Date().toISOString());

            // Add signature files as multipart data
            if (formData.signature) {
                const signatureBlob = dataURLtoBlob(formData.signature);
                formDataToSend.append('signature', signatureBlob, 'signature.png');
            }

            if (formData.agentSignature) {
                const agentSignatureBlob = dataURLtoBlob(formData.agentSignature);
                formDataToSend.append('agentSignature', agentSignatureBlob, 'agent_signature.png');
            }

            console.log('FormData (json):', JSON.stringify(formDataToDebugObject(formDataToSend), null, 2));

            const apiUrl = CRITUTILS + `?action=blrenew&taxpayer=${taxpayerId}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                body: formDataToSend
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Check if response indicates success based on the API response format
            if (result.Status === "Success") {
                setSubmitStatus('success');
                // Store the submitted data before resetting
                setSubmittedData({ ...formData });
                setShowSuccessScreen(true);

                // Reset form
                setFormData({
                    calendarYear: new Date().getFullYear().toString(),
                    licenseType: '',
                    temporaryStartDate: '',
                    seasonalStartDate: '',
                    peddlerType: '',
                    expeditedProcessing: false,
                    applicationReason: '',
                    previousNameLocation: '',
                    ownerChangeDetails: '',
                    legalBusinessName: '',
                    federalEIN: '',
                    physicalAddress: '',
                    mailingAddress: '',
                    businessPhone: '',
                    businessFax: '',
                    businessEmail: '',
                    commenceDate: '',
                    businessDescription: '',
                    businessActivity: '',
                    tobaccoSales: '',
                    liquorSales: '',
                    previousLicense: '',
                    previousLicenseNumber: '',
                    previousBusinessName: '',
                    licenseSuspended: '',
                    licenseSuspendedDetails: '',
                    entityType: '',
                    incorporationState: '',
                    owners: [{ name: '', percentage: '' }],
                    ownerMailingAddress: '',
                    ownerPhone: '',
                    ownerFax: '',
                    ownerEmail: '',
                    primaryContactFirstName: '',
                    primaryContactLastName: '',
                    primaryContactDOB: '',
                    primaryContactPhone: '',
                    primaryContactEmail: '',
                    agentName: '',
                    agentAddress: '',
                    critLease: '',
                    leaseNumber: '',
                    leaseAddress: '',
                    critMember: '',
                    enrollmentNumber: '',
                    otherTribe: '',
                    otherTribeName: '',
                    signature: '',
                    printName: '',
                    signatureDate: '',
                    agentSignature: '',
                    agentSignatureDate: ''
                });

                // Clear signature pads
                if (signaturePadRef.current) signaturePadRef.current.clear();
                if (agentSignaturePadRef.current) agentSignaturePadRef.current.clear();

            } else {
                setError(result.Message || 'Submission failed. Please try again.');
            }

        } catch (error) {
            console.error('Error submitting form:', error);
            setError('An error occurred while submitting the form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Success Screen Component with PDF Download
    const SuccessScreen = () => {
        const generatePDF = () => {
            if (!submittedData) {
                console.error('No submitted data available for PDF generation');
                alert('No data available for PDF generation. Please try submitting the form again.');
                return;
            }

            const pdf = new jsPDF();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            let yPosition = 15;
            const leftMargin = 15;
            const rightMargin = pageWidth - 15;
            const contentWidth = pageWidth - 30;

            // Helper function to capitalize text (first letter capitalized, rest lowercase)
            const capitalizeText = (text) => {
                if (!text) return '';
                const str = String(text);
                return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
            };

            // Helper function to format date to mm/dd/yyyy (US format). For date-only strings (e.g. from
            // HTML type="date" or API YYYY-MM-DD), parse as local date to avoid UTC timezone shifting
            // the day when viewed in US timezones. Used for all PDF dates: temporaryStartDate,
            // seasonalStartDate, commenceDate, primaryContactDOB, agentSignatureDate, signature dates.
            const formatDate = (dateString) => {
                if (!dateString) return '';
                try {
                    const trimmed = String(dateString).trim();
                    // If string looks like yyyy-mm-dd (with optional time), use date part only to avoid timezone shift
                    const dateOnly = trimmed.indexOf(' ') >= 0 ? trimmed.split(' ')[0] : trimmed;
                    const parts = dateOnly.split(/[-\/]/);
                    if (parts.length === 3) {
                        if (parts[0].length === 4) {
                            // yyyy-mm-dd — format as mm/dd/yyyy without Date to avoid timezone shift
                            return `${parts[1].padStart(2, '0')}/${parts[2].padStart(2, '0')}/${parts[0]}`;
                        }
                        // mm/dd/yyyy already
                        return dateOnly;
                    }
                    const date = new Date(dateString);
                    if (isNaN(date.getTime())) return trimmed;
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${month}/${day}/${year}`;
                } catch (e) {
                    return dateString;
                }
            };

            // Helper function to add section header - attractive and professional
            const addSectionHeader = (title) => {
                if (yPosition > pageHeight - 50) {
                    pdf.addPage();
                    yPosition = 15;
                }
                yPosition += 8; // Space before section
                
                // Subtle background bar for section header
                pdf.setFillColor(245, 245, 250);
                pdf.rect(leftMargin, yPosition - 2, contentWidth, 7, 'F');
                
                // Colored accent line
                pdf.setFillColor(132, 0, 40);
                pdf.rect(leftMargin, yPosition - 2, 3, 7, 'F');
                
                // Section title
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(50, 50, 50);
                pdf.text(title, leftMargin + 6, yPosition + 3);
                
                // Bottom border
                pdf.setDrawColor(220, 220, 220);
                pdf.setLineWidth(0.5);
                pdf.line(leftMargin, yPosition + 5, rightMargin, yPosition + 5);
                
                yPosition += 8; // Space after header
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(9);
                pdf.setTextColor(0, 0, 0);
            };

            // Helper function to add field - attractive and clean
            const addField = (label, value, required = false) => {
                // Check if we need a new page before adding field
                if (yPosition > pageHeight - 25) {
                    pdf.addPage();
                    yPosition = 15;
                }
                
                if (value || required) {
                    // Calculate content height
                    const valueText = value || 'N/A';
                    const valueLines = pdf.splitTextToSize(valueText, contentWidth - 6);
                    const fieldHeight = Math.max(10, (valueLines.length * 5) + 10);
                    
                    // Subtle background for field
                    pdf.setFillColor(252, 252, 255);
                    pdf.rect(leftMargin, yPosition - 3, contentWidth, fieldHeight, 'F');
                    
                    // Add label
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(9);
                    pdf.setTextColor(60, 60, 60);
                    const labelText = `${label}:`;
                    pdf.text(labelText, leftMargin + 2, yPosition + 2);
                    yPosition += 8; // Increased space between question and answer
                    
                    // Add value
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(9);
                    pdf.setTextColor(0, 0, 0);
                    valueLines.forEach((line) => {
                        if (yPosition > pageHeight - 20) {
                            pdf.addPage();
                            yPosition = 15;
                        }
                        pdf.text(line, leftMargin + 4, yPosition);
                        yPosition += 5;
                    });
                    
                    yPosition += 4; // Space after field
                }
            };

            // Attractive Professional Header
            // Header background
            pdf.setFillColor(132, 0, 40);
            pdf.rect(0, 0, pageWidth, 32, 'F');
            
            // White text on colored background
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(15);
            pdf.setFont('helvetica', 'bold');
            pdf.text('COLORADO RIVER INDIAN TRIBES', pageWidth / 2, 12, { align: 'center' });
            
            pdf.setFontSize(12);
            pdf.text('Department of Revenue and Finance', pageWidth / 2, 20, { align: 'center' });
            
            // Bottom border of header
            pdf.setDrawColor(180, 0, 50);
            pdf.setLineWidth(1);
            pdf.line(0, 32, pageWidth, 32);
            
            // Document title below header
            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Business License Renewal Application - Summary', pageWidth / 2, 40, { align: 'center' });
            
            // Subtle divider line
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.5);
            pdf.line(leftMargin, 45, rightMargin, 45);
            
            yPosition = 50;

            // Helper: get value as sent to API (toUiValue for transformed fields, raw otherwise)
            const apiVal = (fieldName, rawValue) => (fieldName ? toUiValue(fieldName, rawValue) : rawValue) || '';

            // Calendar Year
            addField('Calendar Year', submittedData.calendarYear, true);

            // 1. RENEWAL LICENSE INFORMATION (same order as form)
            addSectionHeader('1. RENEWAL LICENSE INFORMATION');

            addField('License Type', apiVal('licenseType', submittedData.licenseType), true);
            if (submittedData.temporaryStartDate) addField('Temporary – Renewal period business is to commence', formatDate(submittedData.temporaryStartDate));
            if (submittedData.seasonalStartDate) addField('Seasonal – Renewal period business is to commence', formatDate(submittedData.seasonalStartDate));
            if (submittedData.licenseType === 'Peddler' && submittedData.peddlerType) addField('Peddler Type', apiVal('peddlerType', submittedData.peddlerType));
            if (submittedData.expeditedProcessing) addField('Expedited processing – Next business day renewal processing', apiVal('expeditedProcessing', submittedData.expeditedProcessing) + ' – Fee: $30.00');

            addField('Reason for renewal application', apiVal('applicationReason', submittedData.applicationReason) || submittedData.applicationReason);
            if (submittedData.applicationReason === 'change-name' && submittedData.previousNameLocation) addField('Previous name/location', submittedData.previousNameLocation);
            if (submittedData.applicationReason === 'change-owner' && submittedData.ownerChangeDetails) addField('Owner change details', submittedData.ownerChangeDetails);

            // 2. CURRENT BUSINESS INFORMATION (same order as form, fields 1–16)
            addSectionHeader('2. CURRENT BUSINESS INFORMATION');

            addField('1. Current legal business name', submittedData.legalBusinessName, true);
            addField('2. Federal EIN or social security number', submittedData.federalEIN, true);
            addField('3. Current physical business address: (City, State and Zip)', submittedData.physicalAddress, true);
            addField('4. Current mailing address (if different)', submittedData.mailingAddress);
            addField('5. Current business telephone number', submittedData.businessPhone);
            addField('Current business fax number', submittedData.businessFax);
            addField('6. Current business email address', submittedData.businessEmail);
            addField('7. Date renewal period is to commence', formatDate(submittedData.commenceDate));
            addField('8. Current business description', apiVal('businessDescription', submittedData.businessDescription));
            addField('9. Describe current type of business activity conducted', submittedData.businessActivity);
            addField('10. If tobacco is sold', apiVal('tobaccoSales', submittedData.tobaccoSales));
            addField('11. If liquor is sold', apiVal('liquorSales', submittedData.liquorSales));
            addField('12. Current tribal business license number', submittedData.previousLicenseNumber, true);
            addField('13. Have you previously operated under another business name?', apiVal('previousBusinessName', submittedData.previousBusinessName));
            if (submittedData.previousBusinessName === 'yes' && submittedData.previousNameLocation) addField('Please write previous business name', submittedData.previousNameLocation);
            addField('14. Has your current business license ever been suspended, revoked or renewal denied?', apiVal('licenseSuspended', submittedData.licenseSuspended));
            if (submittedData.licenseSuspended === 'yes' && submittedData.licenseSuspendedDetails) addField('Please explain', submittedData.licenseSuspendedDetails);
            addField('15. Current type of entity', apiVal('entityType', submittedData.entityType));
            addField('16. If a corporation, indicate current state of incorporation', submittedData.incorporationState);

            // 3. CURRENT OWNER AND PRIMARY CONTACT INFORMATION (same order as form, fields 17–20)
            addSectionHeader('3. CURRENT OWNER AND PRIMARY CONTACT INFORMATION');

            if (submittedData.owners && submittedData.owners.length > 0) {
                const ownersList = submittedData.owners
                    .filter(owner => owner.name)
                    .map((owner) => `${owner.name} - ${owner.percentage}%`).join(', ');
                if (ownersList) addField('17. List all current owner(s) and % of ownership or corporation name', ownersList);
            }
            addField('18. Current owner(s) mailing address (City, State, Zip)', submittedData.ownerMailingAddress);
            addField('Current owner(s) telephone', submittedData.ownerPhone);
            addField('Current owner(s) fax', submittedData.ownerFax);
            addField('Current owner(s) email', submittedData.ownerEmail);
            addField('Current primary contact first name', submittedData.primaryContactFirstName);
            addField('Current primary contact last name', submittedData.primaryContactLastName);
            addField('Current primary contact DOB', formatDate(submittedData.primaryContactDOB));
            addField('Current telephone', submittedData.primaryContactPhone);
            addField('Current email', submittedData.primaryContactEmail);

            // 4. ADDITIONAL RENEWAL INFORMATION (same order as form, fields 21–24)
            addSectionHeader('4. ADDITIONAL RENEWAL INFORMATION');

            addField('21. Current agent for service of process – Provide name and address of agent who will accept process on behalf of business', submittedData.agentAddress);
            addField('22. Current CRIT lease / contract status', apiVal('critLease', submittedData.critLease));
            if (submittedData.critLease === 'yes') {
                addField('Current Lease No. or Contract No.', submittedData.leaseNumber);
                addField('Current address listed on Lease or Contract', submittedData.leaseAddress);
            }
            addField('23. Are you a current CRIT tribal member?', apiVal('critMember', submittedData.critMember));
            if (submittedData.critMember === 'yes') addField('Current Enrollment No.', submittedData.enrollmentNumber);
            addField('24. Currently enrolled at another Indian tribe?', apiVal('otherTribe', submittedData.otherTribe));
            if (submittedData.otherTribe === 'yes') addField('Name of current tribe', submittedData.otherTribeName);

            // 5. RENEWAL CONSENT AND VERIFICATION (same order as form)
            addSectionHeader('5. RENEWAL CONSENT AND VERIFICATION');

            addField('Print name', submittedData.printName, true);
            addField('Renewal date', formatDate(new Date()), true);

            if (submittedData.signature) {
                try {
                    // Check if we need a new page before signature
                    if (yPosition > pageHeight - 50) {
                        pdf.addPage();
                        yPosition = 15;
                    }
                    yPosition += 3;
                    
                    // Signature container with subtle background
                    const sigBoxHeight = 32;
                    pdf.setFillColor(252, 252, 255);
                    pdf.rect(leftMargin, yPosition, contentWidth, sigBoxHeight, 'F');
                    
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(9);
                    pdf.setTextColor(60, 60, 60);
                    pdf.text('Applicant Signature:', leftMargin + 3, yPosition + 5);
                    yPosition += 8;
                    
                    // Check if signature image fits on current page
                    if (yPosition + 30 > pageHeight - 30) {
                        pdf.addPage();
                        yPosition = 15;
                        // Redraw background
                        pdf.setFillColor(252, 252, 255);
                        pdf.rect(leftMargin, yPosition, contentWidth, sigBoxHeight, 'F');
                        pdf.setFont('helvetica', 'bold');
                        pdf.setFontSize(9);
                        pdf.text('Applicant Signature:', leftMargin + 3, yPosition + 5);
                        yPosition += 8;
                    }
                    
                    // Add signature image with attractive border
                    pdf.setDrawColor(200, 200, 200);
                    pdf.setLineWidth(0.5);
                    pdf.rect(leftMargin + 3, yPosition, 70, 22, 'S');
                    pdf.addImage(submittedData.signature, 'PNG', leftMargin + 5, yPosition + 1, 66, 20);
                    yPosition += 26;
                    // Signature date (current date) under Applicant Signature
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(9);
                    pdf.setTextColor(60, 60, 60);
                    pdf.text('Signature date: ' + formatDate(new Date()), leftMargin + 3, yPosition + 5);
                    yPosition += 10;
                } catch (error) {
                    addField('Applicant Signature', 'Provided (image error)');
                    addField('Signature date', formatDate(new Date()));
                }
            } else {
                addField('Applicant Signature', 'Not provided');
                addField('Signature date', formatDate(new Date()));
            }

            // Agent Signature with Image
            if (submittedData.agentSignature || submittedData.agentName) {
                // Check if we need a new page before adding agent signature
                if (yPosition > pageHeight - 50) {
                    pdf.addPage();
                    yPosition = 15;
                }

                if (submittedData.agentName) {
                    addField('Agent Name', submittedData.agentName);
                }

                if (submittedData.agentSignature) {
                    try {
                        // Check if we need a new page before signature
                        if (yPosition > pageHeight - 50) {
                            pdf.addPage();
                            yPosition = 15;
                        }
                        yPosition += 3;
                        
                        // Signature container with subtle background
                        const sigBoxHeight = 32;
                        pdf.setFillColor(252, 252, 255);
                        pdf.rect(leftMargin, yPosition, contentWidth, sigBoxHeight, 'F');
                        
                        pdf.setFont('helvetica', 'bold');
                        pdf.setFontSize(9);
                        pdf.setTextColor(60, 60, 60);
                        pdf.text('Agent Signature:', leftMargin + 3, yPosition + 5);
                        yPosition += 8;
                        
                        // Check if signature image fits on current page
                        if (yPosition + 30 > pageHeight - 30) {
                            pdf.addPage();
                            yPosition = 15;
                            // Redraw background
                            pdf.setFillColor(252, 252, 255);
                            pdf.rect(leftMargin, yPosition, contentWidth, sigBoxHeight, 'F');
                            pdf.setFont('helvetica', 'bold');
                            pdf.setFontSize(9);
                            pdf.text('Agent Signature:', leftMargin + 3, yPosition + 5);
                            yPosition += 8;
                        }
                        
                        // Add signature image with attractive border
                        pdf.setDrawColor(200, 200, 200);
                        pdf.setLineWidth(0.5);
                        pdf.rect(leftMargin + 3, yPosition, 70, 22, 'S');
                        pdf.addImage(submittedData.agentSignature, 'PNG', leftMargin + 5, yPosition + 1, 66, 20);
                        yPosition += 26;
                    } catch (error) {
                        addField('Agent Signature', 'Provided (image error)');
                    }
                } else {
                    addField('Agent Signature', 'Not provided');
                }

                if (submittedData.agentSignatureDate) {
                    addField('Date', formatDate(submittedData.agentSignatureDate));
                }
            }

            // Fee Summary
            if (yPosition > pageHeight - 30) {
                pdf.addPage();
                yPosition = 15;
            }
            yPosition += 5;
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);
            pdf.text('TOTAL RENEWAL FEE:', leftMargin, yPosition);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            pdf.setTextColor(132, 0, 40);
            pdf.text(`$${calculateFee(submittedData)}.00`, leftMargin + 50, yPosition);

            // Attractive Footer
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                
                // Footer background
                pdf.setFillColor(250, 250, 250);
                pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
                
                // Footer line
                pdf.setDrawColor(200, 200, 200);
                pdf.setLineWidth(0.5);
                pdf.line(leftMargin, pageHeight - 15, rightMargin, pageHeight - 15);
                
                // Colored accent line
                pdf.setFillColor(132, 0, 40);
                pdf.rect(leftMargin, pageHeight - 15, 20, 1, 'F');
                
                // Page number
                pdf.setFontSize(8);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(100, 100, 100);
                pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
                
                // Submission date on last page
                if (i === totalPages) {
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(132, 0, 40);
                    pdf.setFontSize(8);
                    pdf.text(`Submitted: ${formatDate(new Date())}`, rightMargin, pageHeight - 8, { align: 'right' });
                }
            }

            // Save the PDF
            const fileName = `CRIT_Business_License_Renewal_${submittedData.legalBusinessName || 'renewal'}_${submittedData.calendarYear}.pdf`;
            pdf.save(fileName);
        };

        const handleClose = () => {
            // Close the current tab
            window.close();

            // Fallback - if window.close doesn't work, go to home
            setTimeout(() => {
                navigate('/home');
            }, 100);
        };

        return (
            <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
                <div className="max-w-md mx-auto bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200 text-center">
                    <div className="bg-[#840028] text-white p-6">
                        <h1 className="text-2xl font-bold">Colorado River Indian Tribes</h1>
                    </div>

                    <div className="p-8">
                        <div className="flex justify-center mb-6">
                            <svg className="h-16 w-16 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-800 mb-4">Renewal Submitted Successfully!</h3>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                            <p className="text-green-800 font-semibold">
                                Your business license renewal has been submitted successfully.
                            </p>

                            <p className="text-green-700 text-sm">
                                Reference: <span className="font-bold">Business License Renewal</span>
                            </p>
                        </div>

                        <p className="text-gray-600 mb-6 text-sm">
                            Thank you for submitting your business license renewal application.
                            Your renewal has been received and is being processed.
                            You can download a detailed PDF copy for your records.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={generatePDF}
                                className="bg-blue-600 text-white px-2 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 shadow-md flex items-center justify-center gap-2"
                            >
                                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Renewal Application PDF
                            </button>

                            <button
                                onClick={handleClose}
                                className="bg-[#840028] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6a0020] transition duration-200 shadow-md"
                            >
                                Close
                            </button>
                        </div>

                        <p className="text-xs text-gray-500 mt-4">
                            PDF includes all renewal details with signatures and field values.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    // Loading State
    if (fetchLoading) {
        return (
            <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#840028] mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading your business information...</p>
                </div>
            </div>
        );
    }

    // If showing success screen, return only the success screen
    if (showSuccessScreen) {
        return <SuccessScreen />;
    }

    return (
        <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200">
                {/* Header */}
                <div className="bg-[#840028] text-white p-6">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold tracking-tight">Colorado River Indian Tribes</h1>
                        <h2 className="text-xl font-semibold mt-1">Department of Revenue and Finance</h2>
                        <p className="text-sm mt-3 opacity-90">1000 West Agency Road, Suite A, Parker, Arizona 85344</p>
                        <p className="text-sm opacity-90">Ph (928) 575-1532 • Fax (928) 575-1540 • taxinfo@crit-nsn.gov</p>
                    </div>
                </div>

                <div className="p-6">
                    <h1 className="text-3xl font-bold text-center mb-6 text-gray-800 tracking-tight">
                        Business License Renewal Application
                    </h1>

                    {/* Calendar Year Field - Added at the top */}
                    <div className="mb-6 flex justify-center">
                        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 inline-block">
                            <label className="text-lg font-bold text-gray-800 mr-3">
                                For Calendar Year:
                            </label>
                            <input
                                type="text"
                                name="calendarYear"
                                value={formData.calendarYear}
                                onChange={handleInputChange}
                                className="w-24 border border-gray-300 rounded px-3 py-2 text-center font-bold text-gray-800 focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                placeholder="YYYY"
                                required
                            />
                            <span className="text-sm text-gray-600 ml-2">(must indicate year)</span>
                        </div>
                    </div>

                    {/* Status Messages */}
                    {submitStatus === 'success' && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <svg className="h-5 w-5 text-green-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-green-800 font-semibold">
                                    Your renewal application has been submitted successfully!
                                </p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <svg className="h-5 w-5 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <p className="text-red-800 font-semibold">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Data Loaded Notice */}
                    {apiData && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <svg className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <p className="text-blue-800 font-semibold">
                                    Your existing business information has been loaded. Please review and update any necessary fields.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-amber-800 font-semibold">
                                    Notice: Renewal application must be completed in its entirety and signed.
                                    No modifications of any kind may be made to this renewal form.
                                    Payments received are non-refundable. Renewals must be submitted before expiration.
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* License Type Section */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Type of license renewal requested (Check only one)
                            </h2>

                            <div className="space-y-4">
                                {/* Temporary License */}
                                <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="radio"
                                        name="licenseType"
                                        value="Temporary"
                                        checked={formData.licenseType === 'Temporary'}
                                        onChange={handleInputChange}
                                        className="mt-1 text-[#840028] focus:ring-[#840028]"
                                    />
                                    <div className="flex-1">
                                        <label className="font-semibold text-gray-800">Temporary – Engaged in business four (4) days or less</label>
                                        <p className="text-sm text-gray-600 mt-1">Renewal Fee: $50.00</p>
                                        <div className="mt-2">
                                            <label className="text-sm text-gray-700">Renewal period business is to commence:</label>
                                            <input
                                                type="date"
                                                name="temporaryStartDate"
                                                value={formData.temporaryStartDate}
                                                onChange={handleInputChange}
                                                className="ml-2 border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Seasonal License */}
                                <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="radio"
                                        name="licenseType"
                                        value="Seasonal"
                                        checked={formData.licenseType === 'Seasonal'}
                                        onChange={handleInputChange}
                                        className="mt-1 text-[#840028] focus:ring-[#840028]"
                                    />
                                    <div className="flex-1">
                                        <label className="font-semibold text-gray-800">Seasonal – Engaged in business more than 4 days but less than 3 months</label>
                                        <p className="text-sm text-gray-600 mt-1">Renewal Fee: $75.00</p>
                                        <div className="mt-2">
                                            <label className="text-sm text-gray-700">Renewal period business is to commence:</label>
                                            <input
                                                type="date"
                                                name="seasonalStartDate"
                                                value={formData.seasonalStartDate}
                                                onChange={handleInputChange}
                                                className="ml-2 border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Annual License */}
                                <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="radio"
                                        name="licenseType"
                                        value="Annual"
                                        checked={formData.licenseType === 'Annual'}
                                        onChange={handleInputChange}
                                        className="mt-1 text-[#840028] focus:ring-[#840028]"
                                    />
                                    <div>
                                        <label className="font-semibold text-gray-800">Annual – Engaged in business more than 3 months</label>
                                        <p className="text-sm text-gray-600 mt-1">(Expires December 31st of each year) Renewal Fee: $125.00</p>
                                    </div>
                                </div>

                                {/* Peddler License */}
                                <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="radio"
                                        name="licenseType"
                                        value="Peddler"
                                        checked={formData.licenseType === 'Peddler'}
                                        onChange={handleInputChange}
                                        className="mt-1 text-[#840028] focus:ring-[#840028]"
                                    />
                                    <div className="flex-1">
                                        <label className="font-semibold text-gray-800">Peddler – Must meet the definition of peddler per business & prof. code</label>
                                        <p className="text-sm text-gray-600 mt-1">(Expires December 31st of each year)</p>
                                        <div className="mt-2 space-y-2 ml-4">
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    name="peddlerType"
                                                    value="peddler-retail"
                                                    checked={formData.peddlerType === 'peddler-retail'}
                                                    onChange={handleInputChange}
                                                    className="text-[#840028] focus:ring-[#840028]"
                                                />
                                                <label className="text-gray-700">Retail sales (non-food) Renewal Fee: $100.00</label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    name="peddlerType"
                                                    value="peddler-food"
                                                    checked={formData.peddlerType === 'peddler-food'}
                                                    onChange={handleInputChange}
                                                    className="text-[#840028] focus:ring-[#840028]"
                                                />
                                                <label className="text-gray-700">Retail sales (food) Renewal Fee: $150.00</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Expedited Processing */}
                                <div className="flex items-center space-x-3 mt-4 pt-4 border-t border-gray-200 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="checkbox"
                                        name="expeditedProcessing"
                                        checked={formData.expeditedProcessing}
                                        onChange={handleInputChange}
                                        className="rounded text-[#840028] focus:ring-[#840028]"
                                    />
                                    <label className="font-semibold text-gray-800">Expedited processing – Next business day renewal processing</label>
                                    <span className="text-sm text-gray-600 ml-2">Fee: $30.00</span>
                                </div>
                            </div>
                        </section>

                        {/* Reason for Renewal Section */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Reason for renewal application
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="renewal" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">Standard license renewal</label>
                                    </div>
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="renewal-expansion" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">Renewal with business expansion</label>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="change-name" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">Renewal with change of business name / location (Fee: $100.00)</label>
                                    </div>
                                    {formData.applicationReason === 'change-name' && (
                                        <input
                                            type="text"
                                            name="previousNameLocation"
                                            value={formData.previousNameLocation}
                                            onChange={handleInputChange}
                                            placeholder="Previous name/location"
                                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                        />
                                    )}
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="change-owner" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">Renewal with add/remove owner/partner (Fee: $100.00)</label>
                                    </div>
                                    {formData.applicationReason === 'change-owner' && (
                                        <input
                                            type="text"
                                            name="ownerChangeDetails"
                                            value={formData.ownerChangeDetails}
                                            onChange={handleInputChange}
                                            placeholder="Written authorization required from all owner(s)"
                                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                        />
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Business Information Section */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Current business information
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Field 1: Legal Business Name */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        1. Current legal business name:
                                    </label>
                                    <input
                                        type="text"
                                        name="legalBusinessName"
                                        value={formData.legalBusinessName}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        required
                                    />
                                </div>

                                {/* Field 2: Federal EIN or SSN */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        2. Federal EIN or social security number:
                                    </label>
                                    <input
                                        type="text"
                                        name="federalEIN"
                                        value={formData.federalEIN}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        required
                                    />
                                </div>

                                {/* Field 3: Physical Business Address */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        3. Current physical business address: (City, State and Zip)
                                    </label>
                                    <input
                                        type="text"
                                        name="physicalAddress"
                                        value={formData.physicalAddress}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        required
                                    />
                                </div>

                                {/* Field 4: Mailing Address */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        4. Current mailing address (if different):
                                    </label>
                                    <input
                                        type="text"
                                        name="mailingAddress"
                                        value={formData.mailingAddress}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                {/* Field 5: Business Telephone and Fax */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        5. Current business telephone number:
                                    </label>
                                    <input
                                        type="tel"
                                        name="businessPhone"
                                        value={formData.businessPhone}
                                        onChange={handleInputChange}
                                        placeholder="(XXX) XXX-XXXX"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Current business fax number:
                                    </label>
                                    <input
                                        type="tel"
                                        name="businessFax"
                                        value={formData.businessFax}
                                        onChange={handleInputChange}
                                        placeholder="(XXX) XXX-XXXX"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                {/* Field 6: Business Email */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        6. Current business email address:
                                    </label>
                                    <input
                                        type="email"
                                        name="businessEmail"
                                        value={formData.businessEmail}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                {/* Field 7: Renewal Commence Date */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        7. Date renewal period is to commence:
                                    </label>
                                    <input
                                        type="date"
                                        name="commenceDate"
                                        value={formData.commenceDate}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                {/* Field 8: Business Description */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        8. Current business description:
                                    </label>
                                    <div className="flex flex-wrap gap-4">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="businessDescription"
                                                value="sales"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Sales of goods</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="businessDescription"
                                                value="services"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Services</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="businessDescription"
                                                value="both"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Goods and services</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Field 9: Business Activity */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        9. Describe current type of business activity conducted:
                                    </label>
                                    <textarea
                                        name="businessActivity"
                                        value={formData.businessActivity}
                                        onChange={handleInputChange}
                                        rows={3}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        placeholder="Must include type of merchandise sold or service provided"
                                    />
                                </div>

                                {/* Field 10: Tobacco Sales */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        10. If tobacco is sold:
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="tobaccoSales"
                                                value="retailer"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Retailer</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="tobaccoSales"
                                                value="distributor"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Distributor</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Field 11: Liquor Sales */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        11. If liquor is sold:
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="liquorSales"
                                                value="retailer"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Retailer</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="liquorSales"
                                                value="distributor"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Distributor</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Field 12: Previous Tribal License */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        12. Current tribal business license number:
                                    </label>
                                    <input
                                        type="text"
                                        name="previousLicenseNumber"
                                        value={formData.previousLicenseNumber}
                                        onChange={handleInputChange}
                                        placeholder="Current BL#"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        required
                                    />
                                </div>

                                {/* Field 13: Previous Business Name */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        13. Have you previously operated under another business name?
                                    </label>
                                    <div className="flex gap-4 mb-3">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="previousBusinessName"
                                                value="yes"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="previousBusinessName"
                                                value="no"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">No</span>
                                        </label>
                                    </div>
                                    {formData.previousBusinessName === 'yes' && (
                                        <input
                                            type="text"
                                            name="previousNameLocation"
                                            value={formData.previousNameLocation}
                                            onChange={handleInputChange}
                                            placeholder="Please write previous business name"
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    )}
                                </div>

                                {/* Field 14: License Suspended/Revoked */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        14. Has your current business license ever been suspended, revoked or renewal denied?
                                    </label>
                                    <div className="flex gap-4 mb-3">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="licenseSuspended"
                                                value="yes"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="licenseSuspended"
                                                value="no"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">No</span>
                                        </label>
                                    </div>
                                    {formData.licenseSuspended === 'yes' && (
                                        <textarea
                                            name="licenseSuspendedDetails"
                                            value={formData.licenseSuspendedDetails}
                                            onChange={handleInputChange}
                                            rows={3}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                            placeholder="Please explain"
                                        />
                                    )}
                                </div>

                                {/* Field 15: Type of Entity */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        15. Current type of entity:
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {[
                                            'Sole proprietorship/individual',
                                            'Limited liability company',
                                            'Educational organization',
                                            'Partnership',
                                            'Limited liability partnership',
                                            'Church organization',
                                            'Corporation',
                                            'Unincorporated association',
                                            'Joint venture',
                                            'Non-profit corporation',
                                            'Professional limited liability',
                                            'Tax exempt organization (501(c)(3))'
                                        ].map((entity) => (
                                            <label key={entity} className="flex items-center space-x-2 text-sm p-2 hover:bg-gray-50 rounded">
                                                <input
                                                    type="radio"
                                                    name="entityType"
                                                    value={entity.toLowerCase().replace(/\s+/g, '-')}
                                                    onChange={handleInputChange}
                                                    className="text-[#840028] focus:ring-[#840028]"
                                                />
                                                <span className="text-gray-700">{entity}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {formData.entityType === 'tax-exempt-organization-(501(c)(3))' && (
                                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                            <p className="text-sm text-blue-800">
                                                Notice: Applicant must provide current 26 U.S. C. sec. 501 (c)(3) documentation as proof of tax-exempt status.
                                                If not provided, applicant must pay applicable renewal fee.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Field 16: Incorporation State */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        16. If a corporation, indicate current state of incorporation:
                                    </label>
                                    <input
                                        type="text"
                                        name="incorporationState"
                                        value={formData.incorporationState}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Owner and Primary Contact Information */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Current owner and primary contact information (Required)
                            </h2>

                            {/* Field 17: List All Owners */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    17. List all current owner(s) and % of ownership or corporation name:
                                    <span className="text-xs text-gray-500 ml-1">(If additional space is needed, attach a separate sheet)</span>
                                </label>
                                <div className="space-y-3">
                                    {formData.owners.map((owner, index) => (
                                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                            <input
                                                type="text"
                                                value={owner.name}
                                                onChange={(e) => handleOwnerChange(index, 'name', e.target.value)}
                                                placeholder="Current owner name or corporation name"
                                                className="flex-1 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                            />
                                            <input
                                                type="text"
                                                value={owner.percentage}
                                                onChange={(e) => handleOwnerChange(index, 'percentage', e.target.value)}
                                                placeholder="% Ownership"
                                                className="w-32 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                            />
                                            {formData.owners.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeOwner(index)}
                                                    className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 transition-colors"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addOwner}
                                        className="bg-[#840028] text-white px-4 py-2 rounded hover:bg-[#6a0020] transition-colors font-semibold"
                                    >
                                        Add Owner
                                    </button>
                                </div>
                            </div>

                            {/* Field 18: Owner Mailing Address */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    18. Current owner(s) mailing address (City, State, Zip)
                                </label>
                                <input
                                    type="text"
                                    name="ownerMailingAddress"
                                    value={formData.ownerMailingAddress}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                />
                            </div>

                            {/* Field 19: Owner Contact Information */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Current owner(s) telephone
                                    </label>
                                    <input
                                        type="tel"
                                        name="ownerPhone"
                                        value={formData.ownerPhone}
                                        onChange={handleInputChange}
                                        placeholder="(XXX) XXX-XXXX"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Current owner(s) fax:
                                    </label>
                                    <input
                                        type="tel"
                                        name="ownerFax"
                                        value={formData.ownerFax}
                                        onChange={handleInputChange}
                                        placeholder="(XXX) XXX-XXXX"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Current owner(s) email
                                    </label>
                                    <input
                                        type="email"
                                        name="ownerEmail"
                                        value={formData.ownerEmail}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>
                            </div>

                            {/* Field 20: Primary Contact */}
                            <div className="space-y-4 mb-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Current primary contact first name:
                                        </label>
                                        <input
                                            type="text"
                                            name="primaryContactFirstName"
                                            value={formData.primaryContactFirstName}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Current primary contact last name:
                                        </label>
                                        <input
                                            type="text"
                                            name="primaryContactLastName"
                                            value={formData.primaryContactLastName}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Current primary contact DOB:
                                        </label>
                                        <input
                                            type="date"
                                            name="primaryContactDOB"
                                            value={formData.primaryContactDOB}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Current telephone:
                                        </label>
                                        <input
                                            type="tel"
                                            name="primaryContactPhone"
                                            value={formData.primaryContactPhone}
                                            onChange={handleInputChange}
                                            placeholder="(XXX) XXX-XXXX"
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Current email:
                                        </label>
                                        <input
                                            type="email"
                                            name="primaryContactEmail"
                                            value={formData.primaryContactEmail}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Additional Business Information */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Additional renewal information
                            </h2>

                            {/* Field 21: Agent for Service of Process */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    21. Current agent for service of process – Provide name and address of agent who will accept process on behalf of business:
                                    <span className="text-xs text-gray-500 ml-1">(If no agent, leave blank)</span>
                                </label>
                                <textarea
                                    name="agentAddress"
                                    value={formData.agentAddress}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    placeholder="Current name and address of agent"
                                />
                            </div>

                            {/* Field 22: CRIT Lease/Contract */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    22. Current CRIT lease / contract status
                                </label>
                                <div className="flex gap-4 mb-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="critLease"
                                            value="yes"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">Yes - Currently leased</span>
                                    </label>
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="critLease"
                                            value="no"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">No - Not currently leased</span>
                                    </label>
                                </div>
                                {formData.critLease === 'yes' && (
                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            name="leaseNumber"
                                            value={formData.leaseNumber}
                                            onChange={handleInputChange}
                                            placeholder="Current Lease No. or Contract No."
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                        <input
                                            type="text"
                                            name="leaseAddress"
                                            value={formData.leaseAddress}
                                            onChange={handleInputChange}
                                            placeholder="Current address listed on Lease or Contract"
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Field 23: CRIT Tribal Member */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    23. Are you a current CRIT tribal member?
                                </label>
                                <div className="flex gap-4 mb-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="critMember"
                                            value="yes"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="critMember"
                                            value="no"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">No</span>
                                    </label>
                                </div>
                                {formData.critMember === 'yes' && (
                                    <input
                                        type="text"
                                        name="enrollmentNumber"
                                        value={formData.enrollmentNumber}
                                        onChange={handleInputChange}
                                        placeholder="Current Enrollment No."
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                )}
                            </div>

                            {/* Field 24: Other Tribe */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    24. Currently enrolled at another Indian tribe?
                                </label>
                                <div className="flex gap-4 mb-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="otherTribe"
                                            value="yes"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="otherTribe"
                                            value="no"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">No</span>
                                    </label>
                                </div>
                                {formData.otherTribe === 'yes' && (
                                    <input
                                        type="text"
                                        name="otherTribeName"
                                        value={formData.otherTribeName}
                                        onChange={handleInputChange}
                                        placeholder="Name of current tribe"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                )}
                            </div>
                        </section>

                        {/* Consent and Verification */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Renewal consent and verification
                            </h2>

                            {/* Field 25: Consent Text */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    25. By signing below, I understand and agree to the following conditions of license renewal:
                                </label>
                                <div className="bg-gray-50 p-4 rounded border border-gray-200 text-sm space-y-3">
                                    <p className="text-gray-700">
                                        Each licensee shall comply with all tribal laws, including but not limited to: tribal tax laws,
                                        Indian employment and contracting preference laws and applicable federal law. The licensee is
                                        required to comply with any additional tribal laws as such laws are enacted by the Tribal Council,
                                        and obtain any other licenses or permits required by applicable law.
                                    </p>
                                    <p className="text-gray-700">
                                        Each licensee consents to the jurisdiction of the Tribal Court as to any cause of action arising
                                        in connection with the transaction of any business within the reservation, or any tortious acts
                                        committed in connection with the transaction of any business within the reservation. Each licensee
                                        consents to the service of process of the Tribal Court with respect to all actions over which the
                                        Tribal Court has subject matter jurisdiction, in accordance with the rules of procedure of the Tribal Court.
                                    </p>
                                    <p className="text-gray-700">
                                        Each licensee shall respond in a timely manner to requests by the Department of Revenue for information
                                        about the licensee's business for the purpose of establishing whether the licensee is in compliance with
                                        the terms of the Business and Professions Code.
                                    </p>
                                    <p className="font-semibold text-gray-800">
                                        I declare that I have examined this renewal application and the information contained herein and to the best of
                                        my knowledge and belief, it is true and correct. I swear or affirm that I will comply with all tribal
                                        laws applicable to my business and consent to the jurisdiction of the Tribal Court of the Colorado River
                                        Indian Tribes and service of process in matters arising from the conduct of business.
                                    </p>
                                </div>
                            </div>

                            {/* Signature Section */}
                            <div>
                                <p className="font-semibold mb-4 text-gray-800">Renewal application must be signed by an authorized person:</p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Applicant Signature */}
                                    <div className="border border-gray-300 rounded-lg p-4 bg-white">
                                        <div className="text-center mb-3">
                                            <span className="text-sm font-semibold text-gray-700">Please sign here for renewal *</span>
                                            <span className="text-red-500 ml-1">(Required)</span>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="border border-gray-300 rounded bg-white shadow-sm">
                                                <SignatureCanvas
                                                    ref={signaturePadRef}
                                                    penColor="#0000FF"
                                                    canvasProps={{
                                                        width: 400,
                                                        height: 150,
                                                        className: 'w-full h-40 border-b border-gray-300 signature-canvas'
                                                    }}
                                                    onEnd={handleSignatureEnd}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <button
                                                    type="button"
                                                    onClick={clearSignature}
                                                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                                                >
                                                    Clear signature
                                                </button>
                                                <span className="text-xs text-gray-500">Sign above - Required</span>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Renewal date: *</label>
                                                <input
                                                    type="date"
                                                    name="signatureDate"
                                                    value={formData.signatureDate}
                                                    onChange={handleInputChange}
                                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Print name: *</label>
                                                <input
                                                    type="text"
                                                    name="printName"
                                                    value={formData.printName}
                                                    onChange={handleInputChange}
                                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Agent Signature */}
                                    <div className="border border-gray-300 rounded-lg p-4 bg-white">
                                        <div className="text-center mb-3">
                                            <span className="text-sm font-semibold text-gray-700">Agent for service of process:</span>
                                            <span className="text-gray-500 ml-1">(Optional)</span>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="border border-gray-300 rounded bg-white shadow-sm">
                                                <SignatureCanvas
                                                    ref={agentSignaturePadRef}
                                                    penColor="#0000FF"
                                                    canvasProps={{
                                                        width: 400,
                                                        height: 150,
                                                        className: 'w-full h-40 border-b border-gray-300 signature-canvas'
                                                    }}
                                                    onEnd={handleAgentSignatureEnd}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <button
                                                    type="button"
                                                    onClick={clearAgentSignature}
                                                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                                                >
                                                    Clear signature
                                                </button>
                                                <span className="text-xs text-gray-500">Sign above (if applicable)</span>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Date:</label>
                                                <input
                                                    type="date"
                                                    name="agentSignatureDate"
                                                    value={formData.agentSignatureDate}
                                                    onChange={handleInputChange}
                                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Final Notice */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700 font-semibold">
                                        Notice: Any modifications to this renewal application form or its provisions shall be cause to deny the renewal,
                                        revoke the license or assess penalties. All fees paid are non-refundable. This renewal application and all information
                                        filed with or received by the department shall become the property of the department. Documents or other
                                        information provided to the department will not be returned to the applicant.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Fee Summary */}
                        <section className="bg-gray-100 rounded-lg p-6 border border-gray-300">
                            <h2 className="text-xl font-bold mb-4 text-gray-800">Renewal fee summary</h2>
                            <div className="bg-white rounded-lg p-4 border border-gray-300 shadow-sm">
                                <p className="text-lg font-semibold text-gray-800">Total renewal fee: <span className="text-[#840028]">${calculateFee()}.00</span></p>
                            </div>
                        </section>

                        {/* Submit Button */}
                        <div className="flex justify-center">
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-[#840028] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6a0020] transition duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Submitting...' : 'Submit Renewal Application'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RenewalBusinessLicense;